import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
// TipsAndUpdatesIcon removed (Tips block moved to header popover)
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  ListItemText,
  Snackbar,
  Switch,
  useMediaQuery,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import FindInHtml from '../../components/FindInHtml';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  APP_DATETIME_FORMAT,
  formatAppDateTimeApi,
  formatNoteEditorTimestamp,
} from '../../utils/dateFormats';
import JoditEditor from 'jodit-react';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { createNote, getNote, getNotesPicker, updateNote } from '../../api/notes';
import { TagsInput } from '../../components/workflow/TagsInput';
import type { NotePickerOption } from '../../types/note';
import { Tasks } from "../../components/Tasks.tsx";
import { LinkWithCopy } from '../../components/LinkWithCopy';
import { Note } from '../../types/note';
import { countEmbeddedIframes, normalizeJoditTempHtml, TxtSplitEditor, type TxtSplitEditorHandle } from '../../components/txt-editor';
import { useFocusSyncToast } from '../../utils/useFocusSyncToast';
import { MarkerHighlightTextField } from '../../utils/textMarkerHighlight';
import {
  collectYoutubePreviewCandidates,
  postYoutubeIframeCommand,
  removeYoutubeMediaAtIndex,
  withYoutubeEmbedJsApi,
} from '../../utils/noteMediaHtml';
import {
  isLargePreviewGalleryMode,
  largePreviewButtonFocusSx,
  largePreviewGridRootSx,
  largePreviewGridTileSx,
  largePreviewDialogActionsBarSx,
  largePreviewDialogContentSx,
  largePreviewSideGutterLeftSx,
  largePreviewSideGutterRightSx,
  largePreviewMediaPlayerShellSx,
  largePreviewSideNavBtnSx,
  largePreviewDialogTitleSx,
  largePreviewModalPaperSx,
} from '../../utils/notePreviewUi';
import { LargePreviewDialogTitle, LargePreviewModeToolbar } from '../../utils/LargePreviewKeyboardClue';
import { LargePreviewMediaActions } from '../../utils/LargePreviewMediaActions';
import {
  LARGE_PREVIEW_DIALOG_ATTR,
  useLargePreviewMediaKeyboard,
} from '../../utils/useLargePreviewMediaKeyboard';

type NotesQuickColorKey = 'green' | 'orange' | 'red';
type NoteEditorMode = 'jodit' | 'textarea';
const NOTES_SHOW_HTML_SOURCE_KEY = 'notes.showHtmlSource';
type TxtLargePreviewMode = 'rendered' | 'raw' | 'media' | 'grid';

/** Above this, Jodit init on load can take 10â€“30s (many YouTube/embed iframes). */
const JODIT_IFRAME_SAFE_MAX = 15;


type MemoJoditEditorProps = {
  value: string;
  config: any;
  onBlur: (content: string) => void;
  editorRef: React.Ref<any>;
};
const MemoJoditEditor = React.memo(({ value, config, onBlur, editorRef }: MemoJoditEditorProps) => {
  return (
    <JoditEditor
      ref={editorRef}
      value={value}
      config={config}
      onBlur={onBlur}
    />
  );
});

type RelatedNotesAutocompleteProps = {
  value: Note[];
  onChange: (notes: Note[]) => void;
  disabled?: boolean;
  onBlur?: () => void;
};

const pickerOptionToNote = (option: NotePickerOption): Note => ({
  id: option.id,
  name: option.name,
  description: '',
  status: '',
  created_at: '',
  updated_at: '',
});

const NOTE_STATUS_SUGGESTIONS = [
  'Planning',
  'Active',
  'On hold',
  'In progress',
  'Completed',
  'Archived',
  'Cancelled',
] as const;

const mergePickerOptions = (fetched: NotePickerOption[], selected: Note[]): Note[] => {
  const byId = new Map<string, NotePickerOption>();
  for (const item of selected) {
    byId.set(String(item.id), { id: item.id, name: item.name });
  }
  for (const item of fetched) {
    byId.set(String(item.id), item);
  }
  return Array.from(byId.values())
    .map(pickerOptionToNote)
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }));
};

const RelatedNotesAutocomplete = React.memo(({ value, onChange, disabled, onBlur }: RelatedNotesAutocompleteProps) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();
  const { id } = useParams();

  const fetchPickerOptions = useCallback(
    async (term: string) => {
      if (!id) {
        return;
      }
      try {
        setIsLoading(true);
        const data = await getNotesPicker({
          ignore_note: id,
          ...(term.trim() ? { title_search_term: term.trim() } : {}),
        });
        setOptions(mergePickerOptions(data.notes, value));
      } finally {
        setIsLoading(false);
      }
    },
    [id, value],
  );

  useEffect(() => {
    if (!listOpen) {
      return;
    }
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      void fetchPickerOptions(inputValue);
    }, inputValue.trim() ? 400 : 0);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [inputValue, listOpen, fetchPickerOptions]);

  const handleOpen = () => {
    setListOpen(true);
    void fetchPickerOptions(inputValue);
  };

  const handleClose = () => {
    setListOpen(false);
  };

  return (
    <Autocomplete
      multiple
      options={options}
      open={listOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      disableCloseOnSelect
      getOptionLabel={(option) => option?.name || ''}
      value={value}
      onChange={(_e, val) => onChange(val || [])}
      inputValue={inputValue}
      onInputChange={(_e, val) => setInputValue(val)}
      filterSelectedOptions
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={isLoading}
      disabled={disabled}
      autoSelect={false}
      freeSolo={false}
      clearOnBlur={false}
      onBlur={onBlur}
      sx={{ mb: 2 }}
      renderOption={(props, option, { selected }) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Checkbox checked={selected} />
            <ListItemText primary={option.name} />
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('notes.relatedNotes')}
        />
      )}
    />
  );
});

const SUGGESTED_NOTE_TAGS = ['Zaujímavé'];

const tagsKeyOf = (names: string[]) => [...names].map((n) => n.trim().toLowerCase()).filter(Boolean).sort().join('\u0001');

export const NoteForm: React.FC = () => {
  const [name, setName] = useState('');
  const [status, setStatus] = useState('');
  const [description, setDescription] = useState('');
  const [relatedNotes, setRelatedNotes] = useState<Note[]>([]);
  const [tagNames, setTagNames] = useState<string[]>([]);
  const [notifyAboutChanges, setNotifyAboutChanges] = useState<boolean>(false);
  const [reminderAt, setReminderAt] = useState<Date | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copyNotification, setCopyNotification] = useState<{ open: boolean; text: string }>({ open: false, text: '' });
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [shortcutsAnchorEl, setShortcutsAnchorEl] = useState<HTMLElement | null>(null);
  const [shortcutFilter, setShortcutFilter] = useState('');
  const editorRef = useRef(null);
  const txtSplitEditorRef = useRef<TxtSplitEditorHandle | null>(null);
  const isAutosavingRef = useRef(false);
  const createAutoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [isEditorReady, setIsEditorReady] = useState(false);
  /** Jodit finished init; TXT mode sets this when the split preview is shown. */
  const [isMainEditorReady, setIsMainEditorReady] = useState(false);
  /** Defer embedded Tasks until note + editor/preview are on screen (avoids competing with GET /notes). */
  const [shouldLoadEmbeddedTasks, setShouldLoadEmbeddedTasks] = useState(false);
  const [showSaveEffect, setShowSaveEffect] = useState(false);
  const [showTopProgress, setShowTopProgress] = useState(false);
  const [topProgress, setTopProgress] = useState(0);
  const [celebrationsEnabled, setCelebrationsEnabled] = useState(true);
  const [editorMode, setEditorMode] = useState<NoteEditorMode>(() => {
    try {
      const raw = localStorage.getItem('notes.editorMode');
      if (raw === 'textarea' || raw === 'jodit') {
        return raw;
      }
    } catch {
      /* ignore */
    }
    return 'textarea';
  });
  const [txtLargePreviewOpen, setTxtLargePreviewOpen] = useState(false);
  const [txtLargePreviewMode, setTxtLargePreviewMode] = useState<TxtLargePreviewMode>('rendered');
  const [txtLargePreviewIndex, setTxtLargePreviewIndex] = useState(0);
  const lastSavedRef = useRef<{ name: string; status: string; description: string; relatedIdsKey: string; notify: boolean; reminderKey: string; tagsKey: string } | null>(null);
  const lastAppliedRemoteSyncTsRef = useRef<number>(0);
  const lastAppliedServerUpdatedAtRef = useRef<string>('');
  const pendingServerNoteRef = useRef<Note | null>(null);
  const didHydrateFromServerRef = useRef(false);

  const buildCurrentStateKey = useCallback((override?: {
    name?: string;
    status?: string;
    description?: string;
    related?: Note[];
    notify?: boolean;
    reminder?: Date | null;
    tags?: string[];
  }) => {
    const currentName = override?.name ?? name;
    const currentStatus = override?.status ?? status;
    const currentDescription = override?.description ?? description;
    const currentRelated = override?.related ?? relatedNotes;
    const currentNotify = override?.notify ?? notifyAboutChanges;
    const currentReminder = override?.reminder ?? reminderAt;
    const currentTags = override?.tags ?? tagNames;

    return {
      name: currentName || '',
      status: (currentStatus || '').trim(),
      description: currentDescription || '',
      relatedIdsKey: (currentRelated || []).map(n => n.id).sort((a, b) => a.localeCompare(b)).join(','),
      notify: !!currentNotify,
      reminderKey: currentReminder ? (formatAppDateTimeApi(currentReminder) ?? '') : '',
      tagsKey: tagsKeyOf(currentTags),
    };
  }, [name, status, description, relatedNotes, notifyAboutChanges, reminderAt, tagNames]);

  const hasLocalUnsavedChanges = useCallback(() => {
    const last = lastSavedRef.current;
    if (!last) return false;
    const cur = buildCurrentStateKey();
    return !(
      last.name === cur.name &&
      last.status === cur.status &&
      last.description === cur.description &&
      last.relatedIdsKey === cur.relatedIdsKey &&
      last.notify === cur.notify &&
      last.reminderKey === cur.reminderKey &&
      last.tagsKey === cur.tagsKey
    );
  }, [buildCurrentStateKey]);

  const broadcastNoteSync = useCallback((noteId: string) => {
    try {
      const payload = JSON.stringify({ id: noteId, ts: Date.now() });
      localStorage.setItem(`notes.sync.${noteId}`, payload);
    } catch {
      // ignore storage errors
    }
  }, []);

  const applyServerNoteToForm = useCallback((note: Note) => {
    document.title = t('notes.editNote') + ` ${note.name}`;
    setName(note.name);
    setStatus((note.status ?? '').trim());
    setDescription(note.description);
    setRelatedNotes((note.related_notes || []).map(pickerOptionToNote));
    setNotifyAboutChanges(!!note.notify_about_changes);
    setReminderAt(note.reminder_at ? new Date(note.reminder_at) : null);
    const serverTagNames = (note.tags ?? []).map((tag) => tag.name);
    setTagNames(serverTagNames);
    lastSavedRef.current = {
      name: note.name || '',
      status: (note.status ?? '').trim(),
      description: note.description || '',
      relatedIdsKey: (note.related_notes || []).map(r => r.id).sort((a, b) => a.localeCompare(b)).join(','),
      notify: !!note.notify_about_changes,
      reminderKey: note.reminder_at ? (formatAppDateTimeApi(new Date(note.reminder_at)) ?? '') : '',
      tagsKey: tagsKeyOf(serverTagNames),
    };
    setUpdatedAt(new Date(note.updated_at));
    lastAppliedServerUpdatedAtRef.current = String(note.updated_at ?? '');
  }, [t]);

  const noteQuery = useQuery({
    queryKey: ['note', id],
    queryFn: async () => getNote(String(id)),
    enabled: !!id,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    setIsMainEditorReady(false);
    setShouldLoadEmbeddedTasks(false);
  }, [id]);

  useEffect(() => {
    if (!id) {
      setIsMainEditorReady(true);
      return;
    }
    if (!isEditorReady) {
      setIsMainEditorReady(false);
      return;
    }
    if (editorMode === 'textarea') {
      setIsMainEditorReady(true);
      return;
    }
    // Jodit: wait for afterInit. Do not set false here — child afterInit can run
    // before this parent effect and would be overwritten (Tasks spinner forever).
    const fallbackId = window.setTimeout(() => {
      setIsMainEditorReady(true);
    }, 2000);
    return () => window.clearTimeout(fallbackId);
  }, [editorMode, id, isEditorReady]);

  useEffect(() => {
    if (!id || !isMainEditorReady) {
      setShouldLoadEmbeddedTasks(false);
      return;
    }

    let cancelled = false;
    const enable = () => {
      if (!cancelled) {
        setShouldLoadEmbeddedTasks(true);
      }
    };

    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (win.requestIdleCallback) {
      idleId = win.requestIdleCallback(enable, { timeout: 800 });
    } else {
      timeoutId = setTimeout(enable, 300);
    }

    return () => {
      cancelled = true;
      if (idleId != null && win.cancelIdleCallback) {
        win.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
    };
  }, [id, isMainEditorReady]);

  const onJoditAfterInit = useCallback(() => {
    setIsMainEditorReady(true);
  }, []);

  // Toast like Tasks/EA when we actually apply remote updates (not dirty).
  useFocusSyncToast({
    enabled: !!id && !hasLocalUnsavedChanges(),
    dataUpdatedAt: noteQuery.dataUpdatedAt,
    isRefetching: noteQuery.isRefetching,
    message: 'Note synced from another window',
  });

  // Reusable effect trigger: progress bar, confetti, sound, overlay
  const confettiBurst = useCallback(() => {
    try {
      const duration = 2100;
      const count = 40;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const canvas = document.createElement('canvas');
      canvas.style.position = 'fixed';
      canvas.style.left = '0';
      canvas.style.top = '0';
      canvas.style.pointerEvents = 'none';
      canvas.width = w;
      canvas.height = h;
      canvas.style.zIndex = '1500';
      document.body.appendChild(canvas);
      // schedule canvas fade to start slightly before particles finish so pieces fade while falling
      const fadeDuration = 700;
      const fadeLead = 400; // start fade this many ms before 'duration' ends
      const fadeStart = Math.max(0, duration - fadeLead);
      const fadeTimeout = setTimeout(() => {
        try {
          canvas.style.transition = `opacity ${fadeDuration}ms ease`;
          canvas.style.opacity = '0';
        } catch (err) {}
      }, fadeStart);
      // ensure canvas removed after fade completes (fadeStart + fadeDuration + small buffer)
      setTimeout(() => {
        try { if (canvas.parentNode) canvas.parentNode.removeChild(canvas); } catch (err) {}
        clearTimeout(fadeTimeout);
      }, fadeStart + fadeDuration + 80);
      const ctx = canvas.getContext('2d')!;

      type Particle = { x:number;y:number;vx:number;vy:number;w:number;h:number;angle:number;va:number;color:string;ttl:number };
      const colors = ['#FF6B6B','#FFD93D','#6BCB77','#4D96FF','#A86BFF','#40E0D0'];
      const particles: Particle[] = [];
      for (let i=0;i<count;i++){
        const x = Math.random()*w;
        const y = -20 - Math.random()*h*0.1;
        const vx = (Math.random()-0.5)*6;
        const vy = 2 + Math.random()*6;
        const s = 6 + Math.random()*10;
        particles.push({ x,y,vx,vy,w:s,h:s,angle:Math.random()*Math.PI*2,va:(Math.random()-0.5)*0.2,color:colors[Math.floor(Math.random()*colors.length)],ttl:duration });
      }

      const start = performance.now();
      // ensure canvas starts fully visible for fade
      canvas.style.opacity = '1';
      const frame = (time:number) => {
        const t = time - start;
        ctx.clearRect(0,0,w,h);
        particles.forEach(p=>{
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.06; // gravity
          p.angle += p.va;
          p.ttl -= 16;
          ctx.save();
          ctx.translate(p.x,p.y);
          ctx.rotate(p.angle);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.w/2,-p.h/2,p.w,p.h);
          ctx.restore();
        });
        if (t < duration) requestAnimationFrame(frame);
        else {
          // If we reach the end before scheduled removal, clean up immediately
          try { if (canvas.parentNode) canvas.parentNode.removeChild(canvas); } catch (err) {}
        }
      };
      requestAnimationFrame(frame);
    } catch (err) {
      console.error('confetti error', err);
    }
  }, []);

  const playSaveSound = useCallback(()=>{
    try {
      const AudioCtx = (window.AudioContext || (window as any).webkitAudioContext);
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = 880; // A5
      g.gain.value = 0;
      o.connect(g);
      g.connect(ctx.destination);
      const now = ctx.currentTime;
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.08, now + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      o.start(now);
      o.stop(now + 0.55);
      // close context shortly after
      setTimeout(()=>{ try{ ctx.close(); }catch{} }, 800);
    } catch (err) { console.error('playSaveSound err', err); }
  }, []);

  // --- In-app find dialog state and handlers (delegated to FindInHtml) ---
  const [findOpen, setFindOpen] = useState(false);
  const [originalDescription, setOriginalDescription] = useState<string | null>(null);

  const openFindDialog = useCallback(() => {
    setOriginalDescription(description);
    setFindOpen(true);
  }, [description]);

  const closeFindDialog = useCallback(() => {
    setFindOpen(false);
  }, []);

  const clearHighlights = useCallback(() => {
    if (originalDescription !== null) {
      setDescription(originalDescription);
    }
  }, [originalDescription]);

  const txtPreviewCandidates = useMemo(
    () => collectYoutubePreviewCandidates(normalizeJoditTempHtml(description || '')),
    [description],
  );

  const txtLargePreviewEmbedUrl = useMemo(() => {
    if (!txtPreviewCandidates.length) return '';
    const idx = Math.min(Math.max(0, txtLargePreviewIndex), txtPreviewCandidates.length - 1);
    const raw = txtPreviewCandidates[idx] || '';
    return withYoutubeEmbedJsApi(raw);
  }, [txtLargePreviewIndex, txtPreviewCandidates]);

  const txtLargePreviewGridRootRef = useRef<HTMLDivElement | null>(null);
  const largePreviewIframeRef = useRef<HTMLIFrameElement | null>(null);
  const largePreviewGridIframeRef = useRef<HTMLIFrameElement | null>(null);

  const bindLargePreviewGridIframeRef = useCallback(
    (idx: number) => (el: HTMLIFrameElement | null) => {
      if (idx === txtLargePreviewIndex) {
        largePreviewGridIframeRef.current = el;
      }
    },
    [txtLargePreviewIndex],
  );

  const openLargePreview = useCallback((preferredMode?: TxtLargePreviewMode) => {
    // Prefer media when we have YT/iframe candidates.
    if (txtPreviewCandidates.length > 0) {
      setTxtLargePreviewMode('media');
    } else if (preferredMode) {
      setTxtLargePreviewMode(preferredMode);
    } else {
      setTxtLargePreviewMode('rendered');
    }
    setTxtLargePreviewIndex(0);
    setTxtLargePreviewOpen(true);
  }, [txtPreviewCandidates.length]);

  // When switching to Grid, keep scroll aligned with current media selection.
  useEffect(() => {
    if (!txtLargePreviewOpen) return;
    if (txtLargePreviewMode !== 'grid') return;
    const root = txtLargePreviewGridRootRef.current;
    if (!root) return;
    const id = window.setTimeout(() => {
      try {
        const el = root.querySelector(`[data-grid-index="${txtLargePreviewIndex}"]`) as HTMLElement | null;
        el?.scrollIntoView({ block: 'center', inline: 'center' });
      } catch {
        // ignore
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [txtLargePreviewIndex, txtLargePreviewMode, txtLargePreviewOpen]);

  // Stop audio when moving to another grid tile.
  useEffect(() => {
    if (!txtLargePreviewOpen || txtLargePreviewMode !== 'grid') return;
    const root = txtLargePreviewGridRootRef.current;
    root?.querySelectorAll('iframe').forEach((iframe) => {
      postYoutubeIframeCommand(iframe, 'pauseVideo');
    });
  }, [txtLargePreviewIndex, txtLargePreviewMode, txtLargePreviewOpen]);

  const largePreviewThemed = isLargePreviewGalleryMode(txtLargePreviewMode);

  const {
    context: largePreviewKeyboardContext,
    previewIframeFocusHandlers,
    canYoutubeSpace,
    isYoutubePlaying,
    toggleYoutubePlayback,
  } = useLargePreviewMediaKeyboard({
    open: txtLargePreviewOpen,
    mode: txtLargePreviewMode,
    embedUrl: txtLargePreviewEmbedUrl,
    candidateCount: txtPreviewCandidates.length,
    onIndexChange: setTxtLargePreviewIndex,
    iframeRef: largePreviewIframeRef,
    gridIframeRef: largePreviewGridIframeRef,
    blocked: findOpen,
    onOpenMediaPlayer: () => setTxtLargePreviewMode('media'),
    onOpenMediaGrid: () => setTxtLargePreviewMode('grid'),
  });

  // Render HTML as React while supporting [[copyable]] tokens and copyable links (same UX as NoteView).
  const renderDescriptionAsReact = useCallback((html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const processTextWithCopyable = (text: string, key: string | number): React.ReactNode => {
      const parts = text.split(/(\[\[[^\]]+\]\])/g);
      return parts
        .map((part, index) => {
          if (part.startsWith('[[') && part.endsWith(']]')) {
            const copyableText = part.slice(2, -2);
            return (
              <Box
                key={`${key}-copyable-${index}`}
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: 'action.hover',
                  borderRadius: '4px',
                  padding: '2px 4px',
                }}
              >
                <Box
                  component="span"
                  data-copyable={copyableText}
                  sx={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigator.clipboard
                      .writeText(copyableText)
                      .then(() => {
                        setCopyNotification({ open: true, text: copyableText });
                        setTimeout(() => setCopyNotification({ open: false, text: '' }), 7000);
                      })
                      .catch((err) => console.error('Failed to copy text: ', err));
                  }}
                >
                  {copyableText}
                </Box>
                <Tooltip title="Copy text">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      navigator.clipboard
                        .writeText(copyableText)
                        .then(() => {
                          setCopyNotification({ open: true, text: copyableText });
                          setTimeout(() => setCopyNotification({ open: false, text: '' }), 7000);
                        })
                        .catch((err) => console.error('Failed to copy text: ', err));
                    }}
                    sx={{
                      padding: 0.25,
                      marginLeft: 0.5,
                      minWidth: 'auto',
                      minHeight: 'auto',
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          }
          return part || null;
        })
        .filter(Boolean);
    };

    const processElement = (element: Element, key: string | number): React.ReactNode => {
      if (element.nodeType === Node.TEXT_NODE) {
        const text = element.textContent || '';
        return processTextWithCopyable(text, key);
      }

      if (element.tagName === 'A') {
        const href = element.getAttribute('href') || '';
        const children = Array.from(element.childNodes)
          .map((child, index) => {
            if (child.nodeType === Node.TEXT_NODE) {
              return processTextWithCopyable(child.textContent || '', `${key}-text-${index}`);
            }
            if (child.nodeType === Node.ELEMENT_NODE) {
              return processElement(child as Element, `${key}-${index}`);
            }
            return null;
          })
          .filter(Boolean);
        return (
          <LinkWithCopy key={key} href={href}>
            {children}
          </LinkWithCopy>
        );
      }

      const children = Array.from(element.childNodes)
        .map((child, index) => {
          if (child.nodeType === Node.TEXT_NODE) {
            return processTextWithCopyable(child.textContent || '', `${key}-text-${index}`);
          }
          if (child.nodeType === Node.ELEMENT_NODE) {
            return processElement(child as Element, `${key}-${index}`);
          }
          return null;
        })
        .filter(Boolean);

      const Tag = element.tagName.toLowerCase() as keyof JSX.IntrinsicElements;
      const props: any = { key };

      for (let i = 0; i < element.attributes.length; i++) {
        const attr = element.attributes[i];
        if (attr.name === 'class') {
          props.className = attr.value;
        } else if (attr.name === 'style') {
          const styleObj: { [key: string]: string } = {};
          attr.value.split(';').forEach((declaration) => {
            const [property, value] = declaration.split(':').map((s) => s.trim());
            if (property && value) {
              const camelProperty = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
              styleObj[camelProperty] = value;
            }
          });
          props.style = styleObj;
        } else {
          props[attr.name] = attr.value;
        }
      }

      return React.createElement(Tag, props, ...children);
    };

    const bodyChildren = Array.from(doc.body.childNodes);
    return bodyChildren
      .map((child, index) => {
        if (child.nodeType === Node.TEXT_NODE) {
          return processTextWithCopyable(child.textContent || '', `root-text-${index}`);
        }
        if (child.nodeType === Node.ELEMENT_NODE) {
          return processElement(child as Element, `root-${index}`);
        }
        return null;
      })
      .filter(Boolean);
  }, []);

  const focusMatch = useCallback((index: number) => {
    try {
      setTimeout(() => {
        const sel = document.querySelector(`[data-find-index="${index}"]`);
        if (sel) {
          (sel as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          try {
            const editor = editorRef.current as any;
            if (editor && editor.selection && typeof editor.selection.selectNode === 'function') {
              editor.selection.selectNode(sel);
            }
          } catch (err) {}
        }
      }, 120);
    } catch (err) { console.error('focusMatch err', err); }
  }, []);
  

  const triggerSaveEffects = useCallback(()=>{
    // top progress animation
    setShowTopProgress(true);
    setTopProgress(0);
    const duration = 900;
    const start = performance.now();
    const step = (time:number)=>{
      const t = Math.min(1, (time - start)/duration);
      setTopProgress(Math.round(t*100));
      if (t < 1) requestAnimationFrame(step);
      else {
        setTimeout(()=>{
          setShowTopProgress(false);
          setTopProgress(0);
        }, 120);
      }
    };
    requestAnimationFrame(step);

    // overlay
    setShowSaveEffect(true);
    setTimeout(()=> setShowSaveEffect(false), duration + 200);

    // confetti + sound (respect user preference)
    try {
      if (celebrationsEnabled) {
        confettiBurst();
        playSaveSound();
      }
    } catch (err) {
      console.error('triggerSaveEffects inner err', err);
    }
  }, [confettiBurst, playSaveSound, celebrationsEnabled]);

  // Load persisted preference on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('notes.celebrationsEnabled');
      if (raw === null) return;
      setCelebrationsEnabled(raw === '1' || raw === 'true');
    } catch (err) {
      // ignore localStorage errors
    }
  }, []);

  const handleToggleCelebrations = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.checked;
    setCelebrationsEnabled(val);
    try { localStorage.setItem('notes.celebrationsEnabled', val ? '1' : '0'); } catch (err) { /* ignore */ }
  }, []);


  const handleToggleEditorMode = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next: NoteEditorMode = e.target.checked ? 'textarea' : 'jodit';
    setEditorMode(next);
    try { localStorage.setItem('notes.editorMode', next); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!id) return;

    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key !== `notes.sync.${id}`) return;
      if (!event.newValue) return;

      try {
        const parsed = JSON.parse(event.newValue) as { id?: string; ts?: number };
        const ts = typeof parsed.ts === 'number' ? parsed.ts : 0;
        if (!ts || ts <= lastAppliedRemoteSyncTsRef.current) return;
        lastAppliedRemoteSyncTsRef.current = ts;

        // Local broadcast: just refetch. Dirty-guard is handled in the noteQuery effect below.
        void noteQuery.refetch();
      } catch {
        // ignore malformed payload
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [id, noteQuery.refetch]);

  useEffect(() => {
    if (!id) {
      document.title = t('notes.createNew');
      setIsEditorReady(true);
      return;
    }

    if (noteQuery.isError) {
      setError(t('common.errorFetchingData'));
      setIsEditorReady(true);
      return;
    }

    const note = noteQuery.data as Note | undefined;
    if (!note) {
      return;
    }

    const serverUpdatedAt = String(note.updated_at ?? '');
    const hasServerChanged = serverUpdatedAt && serverUpdatedAt !== lastAppliedServerUpdatedAtRef.current;
    const isDirty = hasLocalUnsavedChanges();

    // First hydrate always applies server state.
    if (!didHydrateFromServerRef.current) {
      didHydrateFromServerRef.current = true;
      applyServerNoteToForm(note);
      const iframeCount = countEmbeddedIframes(note.description ?? '');
      if (iframeCount > JODIT_IFRAME_SAFE_MAX) {
        setEditorMode('textarea');
        enqueueSnackbar(
          t('notes.heavyNoteTxtMode', {
            count: iframeCount,
            defaultValue:
              'TXT mode â€” {{count}} embeds in this note; visual editor would load very slowly.',
          }),
          { variant: 'info', autoHideDuration: 8000 },
        );
      }
      setIsEditorReady(true);
      return;
    }

    if (!hasServerChanged) {
      setIsEditorReady(true);
      return;
    }

    if (isDirty) {
      pendingServerNoteRef.current = note;
      enqueueSnackbar('Note updated elsewhere (not applied).', {
        variant: 'info',
        action: (snackbarId) => (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              const pending = pendingServerNoteRef.current;
              if (pending) {
                applyServerNoteToForm(pending);
                pendingServerNoteRef.current = null;
              }
              closeSnackbar(snackbarId);
            }}
          >
            Reload
          </Button>
        ),
      });
      setIsEditorReady(true);
      return;
    }

    applyServerNoteToForm(note);
    setIsEditorReady(true);
  }, [applyServerNoteToForm, enqueueSnackbar, hasLocalUnsavedChanges, id, noteQuery.data, noteQuery.isError, t]);

  const canAutosaveNote = useCallback((override?: {
    name?: string;
    status?: string;
    description?: string;
    related?: Note[];
    notify?: boolean;
    reminder?: Date | null;
    tags?: string[];
  }) => {
    const state = buildCurrentStateKey(override);
    return Boolean(state.name.trim() && state.description.trim());
  }, [buildCurrentStateKey]);

  const persistNote = useCallback(async (
    override?: { name?: string; status?: string; description?: string; related?: Note[]; notify?: boolean; reminder?: Date | null; tags?: string[] },
    options: { isAutoSave?: boolean; showSuccessToast?: boolean } = {},
  ): Promise<boolean> => {
    const isAutoSave = options.isAutoSave ?? false;
    const showSuccessToast = options.showSuccessToast ?? !isAutoSave;

    const currentName = override?.name ?? name;
    const currentStatus = (override?.status ?? status ?? '').trim();
    const currentDescription = override?.description ?? description;
    const currentRelated = override?.related ?? relatedNotes;
    const currentNotify = override?.notify ?? notifyAboutChanges;
    const currentReminder = override?.reminder ?? reminderAt;

    const currentRelatedIdsKey = currentRelated.map(n => n.id).sort((a, b) => a.localeCompare(b)).join(',');
    const currentReminderKey = currentReminder ? (formatAppDateTimeApi(currentReminder) ?? '') : '';
    const currentTags = override?.tags ?? tagNames;
    const currentTagsKey = tagsKeyOf(currentTags);
    const last = lastSavedRef.current;

    if (last &&
        last.name === (currentName || '') &&
        last.status === currentStatus &&
        last.description === (currentDescription || '') &&
        last.relatedIdsKey === currentRelatedIdsKey &&
        last.notify === !!currentNotify &&
        last.reminderKey === currentReminderKey &&
        last.tagsKey === currentTagsKey) {
      return true;
    }

    if (!currentName.trim() || !currentDescription.trim()) {
      if (!currentDescription.trim()) {
        setDescriptionError(t('validation.required'));
      }
      return false;
    }

    setDescriptionError(null);

    if (isAutosavingRef.current) {
      return false;
    }

    const noteData = {
      name: currentName,
      status: currentStatus,
      description: currentDescription,
      related_note_ids: currentRelated.map(n => n.id),
      notify_about_changes: currentNotify,
      reminder_at: formatAppDateTimeApi(currentReminder),
      tags: currentTags,
    };

    try {
      isAutosavingRef.current = true;
      setIsLoading(!isAutoSave);

      if (id) {
        await updateNote(id, noteData);
        broadcastNoteSync(id);
      } else {
        const created = await createNote(noteData);
        broadcastNoteSync(created.id);
        navigate(`/notes/${created.id}/edit`, { replace: true });
      }

      lastSavedRef.current = {
        name: currentName || '',
        status: currentStatus,
        description: currentDescription || '',
        relatedIdsKey: currentRelatedIdsKey,
        notify: !!currentNotify,
        reminderKey: currentReminderKey,
        tagsKey: currentTagsKey,
      };

      setUpdatedAt(new Date());

      if (showSuccessToast) {
        enqueueSnackbar(id ? t('notes.updateSuccess') : t('notes.createSuccess'), { variant: 'success' });
      }

      triggerSaveEffects();
      return true;
    } catch (error) {
      setError(t('common.errorSavingData'));
      console.error('Error saving note:', error);
      return false;
    } finally {
      isAutosavingRef.current = false;
      setIsLoading(false);
    }
  }, [
    name,
    status,
    description,
    relatedNotes,
    notifyAboutChanges,
    reminderAt,
    tagNames,
    id,
    navigate,
    t,
    enqueueSnackbar,
    triggerSaveEffects,
    broadcastNoteSync,
  ]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    await persistNote(undefined, { isAutoSave: false, showSuccessToast: true });
  }, [persistNote]);

  const handleRelatedNotesChange = async (value: Note[]) => {
    setRelatedNotes(value);
    await autosave({ related: value });
  };

  const autosave = useCallback(async (override?: { name?: string; status?: string; description?: string; related?: Note[]; notify?: boolean; reminder?: Date | null; tags?: string[] }) => {
    if (!canAutosaveNote(override)) {
      return;
    }

    await persistNote(override, {
      isAutoSave: true,
      showSuccessToast: Boolean(id),
    });
  }, [canAutosaveNote, persistNote, id]);

  useEffect(() => {
    if (id) {
      return;
    }

    if (!canAutosaveNote()) {
      return;
    }

    const state = buildCurrentStateKey();
    const last = lastSavedRef.current;
    if (last &&
        last.name === state.name &&
        last.status === state.status &&
        last.description === state.description &&
        last.relatedIdsKey === state.relatedIdsKey &&
        last.notify === state.notify &&
        last.reminderKey === state.reminderKey &&
        last.tagsKey === state.tagsKey) {
      return;
    }

    if (createAutoSaveTimerRef.current) {
      clearTimeout(createAutoSaveTimerRef.current);
    }

    createAutoSaveTimerRef.current = setTimeout(() => {
      void persistNote(undefined, { isAutoSave: true, showSuccessToast: false });
    }, 900);

    return () => {
      if (createAutoSaveTimerRef.current) {
        clearTimeout(createAutoSaveTimerRef.current);
      }
    };
  }, [id, name, status, description, relatedNotes, notifyAboutChanges, reminderAt, tagNames, canAutosaveNote, buildCurrentStateKey, persistNote]);

  useEffect(() => () => {
    if (createAutoSaveTimerRef.current) {
      clearTimeout(createAutoSaveTimerRef.current);
    }
  }, []);

  const toggleTextCase = useCallback(() => {
    if (editorRef.current && !isLoading) {
      const editor: any = editorRef.current;
      const selection = editor.selection;

      if (selection && !selection.isCollapsed()) {
        const selectedText = selection.sel?.toString() || editor.selection.getHTML();

        if (selectedText && typeof selectedText === 'string') {
          const plainText = selectedText.replace(/<[^>]*>/g, '');

          if (plainText.trim()) {
            const isUpperCase = plainText === plainText.toUpperCase() &&
                plainText !== plainText.toLowerCase();
            const newText = isUpperCase ? plainText.toLowerCase() : plainText.toUpperCase();

            editor.selection.insertHTML(newText);
          }
        }
      }
    }
  }, []);

  const makeSelectionCopyable = useCallback((editor: any) => {
    try {
      const selection = editor.selection?.sel;
      if (selection && !selection.isCollapsed) {
        const selectedText = selection.toString();
        try {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(`[[${selectedText}]]`));
        } catch (err) {
          editor.selection.insertHTML(`[[${selectedText}]]`);
        }
      } else {
        // No selection: insert placeholder
        editor.selection.insertHTML('[[text to copy]]');
      }
    } catch (err) {
      console.error('makeSelectionCopyable err', err);
    }
  }, []);

  const shortcuts = useMemo(() => ([
    { group: 'editor', key: 'Alt+F', desc: t('notes.find') || 'Find in note' },
    { group: 'editor', key: 'Alt+Q', desc: t('tips.insertTimestampShortcut', { btn: 'Alt+Q' }) || 'Alt+Q to insert timestamp' },
    { group: 'editor', key: 'Alt+S', desc: t('tips.saveShortcut', { btn: 'Alt+S' }) || 'Press Alt+S to save quickly' },
    { group: 'editor', key: 'Alt+U', desc: t('tips.toggleShortcut', { btn: 'Alt+U' }) || 'Alt+U to toggle text case (select text first)' },
    { group: 'editor', key: 'Alt+G / Alt+O / Alt+R', desc: 'Background color (Green / Orange / Red)' },
    { group: 'editor', key: 'Alt+Shift+G / Alt+Shift+O / Alt+Shift+R', desc: 'Text color (Green / Orange / Red)' },
    { group: 'editor', key: 'Alt+V', desc: 'Open large preview' },
    { group: 'editor', key: 'Alt+Shift+V', desc: t('notes.view') || 'View note in new tab' },
    { group: 'editor', key: 'Alt+W', desc: t('tips.makeCopyable') || 'Make text copyable - wrap selected text with [[brackets]]' },
    { group: 'global', key: 'Alt+N', desc: t('shortcuts.newNote') || 'New note' },
    { group: 'global', key: '/', desc: t('tips.focusSearch') || 'Press / to focus global note search' },
  ]), [t]);

  const openShortcuts = (el: HTMLElement) => setShortcutsAnchorEl(el);
  const closeShortcuts = () => {
    setShortcutsAnchorEl(null);
    setShortcutFilter('');
  };

  const insertTimestamp = useCallback(() => {
    if (editorRef.current) {
      const date = formatNoteEditorTimestamp();
      const html = `<span style='color:#40E0D0; font-size:8px;'>${date}</span>`;
      const editor = editorRef.current as any;
      editor.selection.insertHTML(html);
    }
  }, []);


  const notesQuickPalette = useMemo(
    () => ({
      text: {
        green: '#1f5d2a',
        orange: '#8a4b00',
        red: '#8a1f1f',
      },
      background: {
        green: '#c8e6c9',
        orange: '#ffd8a8',
        red: '#f5c2c7',
      },
    }),
    [],
  );

  const getNotesJodit = useCallback(() => {
    const refValue = editorRef.current as any;
    if (refValue?.selection?.insertHTML) {
      return refValue;
    }
    return refValue?.editor ?? null;
  }, []);

  const handleRemovePreviewMedia = useCallback(
    (candidateIndex: number) => {
      if (candidateIndex < 0 || candidateIndex >= txtPreviewCandidates.length) return;

      const nextDescription = removeYoutubeMediaAtIndex(description || '', candidateIndex);
      if (nextDescription === description) {
        enqueueSnackbar(
          t('notes.removeVideoFailed', { defaultValue: 'Could not find this video in the note.' }),
          { variant: 'warning' },
        );
        return;
      }

      setDescription(nextDescription);
      if (editorMode === 'jodit') {
        const editor = getNotesJodit();
        if (editor && typeof editor.value === 'string') {
          editor.value = nextDescription;
        }
      }
      if (!nextDescription.trim()) {
        setDescriptionError(t('validation.required'));
      } else {
        setDescriptionError(null);
      }
      autosave({ description: nextDescription });
      setTxtLargePreviewIndex((current) => {
        if (candidateIndex < current) return current - 1;
        if (candidateIndex === current) return Math.max(0, current - 1);
        return current;
      });
      enqueueSnackbar(
        t('notes.removeVideoDone', { defaultValue: 'Video removed from note.' }),
        { variant: 'success' },
      );
    },
    [autosave, description, editorMode, enqueueSnackbar, getNotesJodit, t, txtPreviewCandidates],
  );

  const applyNotesInlineStyle = useCallback(
    (styleType: 'color' | 'background-color', color: string) => {
      const editor = getNotesJodit();
      if (!editor?.selection?.insertHTML) {
        return;
      }

      try {
        if (typeof editor.focus === 'function') {
          editor.focus();
        } else if (editor.editor && typeof editor.editor.focus === 'function') {
          editor.editor.focus();
        }
      } catch {}

      const selection = editor.selection;
      const selectedHtml = typeof selection.html === 'function' ? selection.html() : '';
      const selectedText = selection.sel?.toString?.() ?? '';
      const content = (selectedHtml || selectedText).trim();
      const safeContent = content || 'text';

      editor.selection.insertHTML(`<span style="${styleType}: ${color};">${safeContent}</span>`);

      // Keep controlled state in sync; autosave still happens on blur.
      const nextDescription = typeof editor.value === 'string' ? editor.value : '';
      setDescription(nextDescription);
    },
    [getNotesJodit],
  );

  const applyNotesQuickColor = useCallback(
    (target: 'text' | 'background', colorKey: NotesQuickColorKey) => {
      if (target === 'text') {
        applyNotesInlineStyle('color', notesQuickPalette.text[colorKey]);
        return;
      }
      applyNotesInlineStyle('background-color', notesQuickPalette.background[colorKey]);
    },
    [applyNotesInlineStyle, notesQuickPalette],
  );

  const config: any = useMemo(() => ({
    readonly: false,
    height: 400,
    toolbar: true,
    spellcheck: false,
    language: 'en',
    toolbarButtonSize: 'middle',
    toolbarAdaptive: false,
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_clear_html',
    buttons: [
      'source', '|',
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'image', 'table', 'link', '|',
      'align', '|',
      'undo', 'redo', '|',
      'hr', 'eraser', 'copyformat', '|',
      {
        name: 'notesTextGreen',
        tooltip: 'Text color: Green',
        icon: `<svg viewBox="0 0 24 24" width="18" height="18"><circle cx="12" cy="12" r="7" fill="${notesQuickPalette.text.green}" stroke="#999" stroke-width="1"/></svg>`,
        exec: () => applyNotesQuickColor('text', 'green'),
      },
      {
        name: 'notesTextOrange',
        tooltip: 'Text color: Orange',
        icon: `<svg viewBox="0 0 24 24" width="18" height="18"><circle cx="12" cy="12" r="7" fill="${notesQuickPalette.text.orange}" stroke="#999" stroke-width="1"/></svg>`,
        exec: () => applyNotesQuickColor('text', 'orange'),
      },
      {
        name: 'notesTextRed',
        tooltip: 'Text color: Red',
        icon: `<svg viewBox="0 0 24 24" width="18" height="18"><circle cx="12" cy="12" r="7" fill="${notesQuickPalette.text.red}" stroke="#999" stroke-width="1"/></svg>`,
        exec: () => applyNotesQuickColor('text', 'red'),
      },
      {
        name: 'notesBgGreen',
        tooltip: 'Background: Green',
        icon: `<svg viewBox="0 0 24 24" width="18" height="18"><rect x="5" y="5" width="14" height="14" rx="3" fill="${notesQuickPalette.background.green}" stroke="#999" stroke-width="1"/></svg>`,
        exec: () => applyNotesQuickColor('background', 'green'),
      },
      {
        name: 'notesBgOrange',
        tooltip: 'Background: Orange',
        icon: `<svg viewBox="0 0 24 24" width="18" height="18"><rect x="5" y="5" width="14" height="14" rx="3" fill="${notesQuickPalette.background.orange}" stroke="#999" stroke-width="1"/></svg>`,
        exec: () => applyNotesQuickColor('background', 'orange'),
      },
      {
        name: 'notesBgRed',
        tooltip: 'Background: Red',
        icon: `<svg viewBox="0 0 24 24" width="18" height="18"><rect x="5" y="5" width="14" height="14" rx="3" fill="${notesQuickPalette.background.red}" stroke="#999" stroke-width="1"/></svg>`,
        exec: () => applyNotesQuickColor('background', 'red'),
      },
      {
        name: 'toggleText',
        icon: '<svg class="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-1phnduy" focusable="false" aria-hidden="true" viewBox="0 0 24 24"><path d="M.99 19h2.42l1.27-3.58h5.65L11.59 19h2.42L8.75 5h-2.5zm4.42-5.61L7.44 7.6h.12l2.03 5.79zM20 11h3v2h-3v3h-2v-3h-3v-2h3V8h2z"></path></svg>',
        tooltip: 'Toggle selected text',
        exec: () => toggleTextCase(),
      },
      {
        name: 'copyableText',
        icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>',
        tooltip: 'Make text copyable - wrap selected text with [[brackets]]',
        exec: (editor: any) => {
          makeSelectionCopyable(editor);
        },
      },
      {
        name: 'insertDate',
        tooltip: 'Insert Date',
        icon: '<svg class="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-1phnduy" focusable="false" aria-hidden="true" viewBox="0 0 24 24"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8"></path><path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"></path></svg>',
        exec: () => insertTimestamp(),
      },
      '|',
      'symbol', 'fullsize', 'print', 'about',
    ],
    removeButtons: [],
    zIndex: 0,
    maxWidth: '100%',
    minHeight: 300,
    minWidth: 300,
    style: {
      background: '#fff'
    },
    events: {
      afterInit: onJoditAfterInit,
    },
  }), [applyNotesQuickColor, makeSelectionCopyable, notesQuickPalette, onJoditAfterInit, toggleTextCase, insertTimestamp]);


  const onJoditBlur = useCallback((newContent: string) => {
    const normalized = normalizeJoditTempHtml(newContent);
    setDescription(normalized);
    if (!normalized.trim()) {
      setDescriptionError(t('validation.required'));
    } else {
      setDescriptionError(null);
    }
    autosave({ description: normalized });
  }, [t, autosave]);

  const handleTxtEditorChange = useCallback((next: string) => {
    setDescription(next);
    if (!next.trim()) {
      setDescriptionError(t('validation.required'));
    } else {
      setDescriptionError(null);
    }
  }, [t]);

  const onTextareaBlur = useCallback((newContent: string) => {
    setDescription(newContent);
    if (!newContent.trim()) {
      setDescriptionError(t('validation.required'));
    } else {
      setDescriptionError(null);
    }
    return autosave({ description: newContent });
  }, [t, autosave]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const txtEditor = txtSplitEditorRef.current;
      // We deliberately handle the editor shortcuts even when focus is inside the editor
      // so users can press Alt+S / Alt+U / Alt+Q / Alt+F / Alt+V while editing.
      try {
        const key = e.key.toLowerCase();
        const isQuickColorKey = key === 'g' || key === 'o' || key === 'r';
        const notesQuickColorMap: Record<'g' | 'o' | 'r', NotesQuickColorKey> = {
          g: 'green',
          o: 'orange',
          r: 'red',
        };

        if ((e.altKey && key === 's') || (e.metaKey && key === 's')) {
          e.preventDefault();
          if (!isLoading && description.trim()) {
            handleSubmit(e as unknown as React.FormEvent);
          }
          return;
        }

        if ((e.altKey && key === 'u') || (e.metaKey && key === 'u')) {
          e.preventDefault();
          if (editorMode === 'textarea') {
            if (txtEditor?.isPreviewEditableFocused()) {
              txtEditor.togglePreviewSelectionCase();
            } else {
              txtEditor?.toggleTextareaSelectionCase();
            }
          } else {
            toggleTextCase();
          }
          return;
        }

        if ((e.altKey && key === 'q') || (e.metaKey && key === 'q')) {
          e.preventDefault();
          if (editorMode === 'textarea') {
            txtEditor?.insertTimestamp();
          } else {
            insertTimestamp();
          }
          return;
        }

        // Match External Assets shortcuts:
        // Alt+G/O/R -> background, Alt+Shift+G/O/R -> text.
        // Only when focus is inside the active editor surface.
        if (e.altKey && !e.metaKey && !e.ctrlKey && isQuickColorKey) {
          const active = document.activeElement as HTMLElement | null;
          const previewRoot = txtEditor?.previewContentEditableRef.current;
          const textareaEl = txtEditor?.descriptionTextareaRef.current;
          const inPreview =
            editorMode === 'textarea' &&
            !!previewRoot &&
            !!active &&
            (active === previewRoot || previewRoot.contains(active));
          const inTextarea =
            editorMode === 'textarea' &&
            !!textareaEl &&
            active === textareaEl;
          const inJodit =
            editorMode !== 'textarea' &&
            !!active &&
            (active.closest?.('.jodit-container') ||
              active.closest?.('.jodit-wysiwyg') ||
              (active as any).isContentEditable);
          if (!inTextarea && !inJodit && !inPreview) return;

          e.preventDefault();
          const colorKey = notesQuickColorMap[key as 'g' | 'o' | 'r'];
          if (e.shiftKey) {
            if (inPreview) {
              txtEditor?.applyPreviewInlineStyle('color', notesQuickPalette.text[colorKey]);
            } else if (editorMode === 'textarea') {
              txtEditor?.applyTextareaInlineStyle('color', notesQuickPalette.text[colorKey]);
            } else {
              applyNotesQuickColor('text', colorKey);
            }
          } else {
            if (inPreview) {
              txtEditor?.applyPreviewInlineStyle('background-color', notesQuickPalette.background[colorKey]);
            } else if (editorMode === 'textarea') {
              txtEditor?.applyTextareaInlineStyle('background-color', notesQuickPalette.background[colorKey]);
            } else {
              applyNotesQuickColor('background', colorKey);
            }
          }
          return;
        }

        // Alt+W -> wrap selected text with [[...]] (make copyable)
        if ((e.altKey && key === 'w') || (e.metaKey && key === 'w')) {
          e.preventDefault();
          if (editorMode === 'textarea') {
            if (txtEditor?.isPreviewEditableFocused()) {
              txtEditor.wrapPreviewSelectionCopyable();
            } else {
              txtEditor?.wrapTextareaSelectionCopyable();
            }
          } else {
            try {
              const editor: any = editorRef.current;
              if (editor) {
                makeSelectionCopyable(editor);
              }
            } catch (err) {
              console.error('Alt+W wrap error', err);
            }
          }
          return;
        }

        // Tab â€” jump to end of synced selection in the other pane (preview â†” HTML source).
        if (
          key === 'tab' &&
          !e.altKey &&
          !e.ctrlKey &&
          !e.metaKey &&
          editorMode === 'textarea' &&
          txtEditor?.showHtmlSource
        ) {
          const sync = txtEditor.txtEditorSelectionRangeRef.current;
          if (sync && sync.start !== sync.end) {
            const active = document.activeElement as HTMLElement | null;
            const previewRoot = txtEditor.previewContentEditableRef.current;
            const textareaEl = txtEditor.descriptionTextareaRef.current;
            const inPreview =
              !!previewRoot &&
              !!active &&
              (active === previewRoot || previewRoot.contains(active));
            const inTextarea = !!textareaEl && active === textareaEl;
            if (inPreview || inTextarea) {
              e.preventDefault();
              if (inPreview) {
                txtEditor.focusHtmlSourceAtSyncEnd();
              } else {
                txtEditor.focusPreviewAtSyncEnd();
              }
              return;
            }
          }
        }

        // Alt+F -> toggle in-app Find (match view behavior: inline on desktop)
        if (e.altKey && key === 'f') {
          e.preventDefault();
          if (findOpen) {
            setFindOpen(false);
          } else {
            // preserve the current description before opening so Clear can restore
            setOriginalDescription(description);
            setFindOpen(true);
          }
          return;
        }

        // Alt+V -> large preview; Alt+Shift+V -> view note in new tab
        if (e.altKey && !e.metaKey && !e.ctrlKey && key === 'v') {
          e.preventDefault();
          if (e.shiftKey) {
            if (id) window.open(`/notes/${id}`, '_blank');
          } else {
            openLargePreview(editorMode === 'textarea' ? undefined : 'rendered');
          }
          return;
        }

        // For any other keys, avoid intercepting when typing in inputs/textarea/contentEditable
        try {
          const target = e.target as HTMLElement | null;
          if (target) {
            const tag = target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || ((target as HTMLElement & { isContentEditable?: boolean }).isContentEditable)) return; // don't intercept while typing
          }
        } catch (err) { /* ignore */ }
      } catch (err) {
        // swallow unexpected errors in key handler
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    applyNotesQuickColor,
    description,
    editorMode,
    findOpen,
    handleSubmit,
    id,
    insertTimestamp,
    isLoading,
    makeSelectionCopyable,
    notesQuickPalette,
    openLargePreview,
    toggleTextCase,
  ]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Full-screen save effect overlay (non-interactive) */}
      <style>{`@keyframes noteSaveFlash { 0% { opacity: 0.95; transform: scale(1); } 40% { opacity: 0.65; transform: scale(1.02); } 100% { opacity: 0; transform: scale(1.04); } }`}</style>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: '#40E0D0',
          opacity: showSaveEffect ? 0.95 : 0,
          transition: 'opacity 1200ms ease',
          pointerEvents: 'none',
          zIndex: 1400,
          boxShadow: 'inset 0 0 200px 40px rgba(64,224,208,0.25)',
          backdropFilter: 'blur(1px)',
          animation: showSaveEffect ? 'noteSaveFlash 1200ms ease-out' : undefined,
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4">
          {id ? t('notes.editNote') : t('notes.createNote')}
        </Typography>
        {name && (
          <Tooltip title={`Copy note name: "${name}"`}>
            <IconButton 
              onClick={() => {
                navigator.clipboard.writeText(name).then(() => {
                  setCopyNotification({ open: true, text: name });
                  setTimeout(() => setCopyNotification({ open: false, text: '' }), 2000);
                }).catch(err => 
                  console.error('Failed to copy note name: ', err)
                );
              }}
              color="secondary"
              size="small"
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        )}
        {id && (
          <Tooltip title={t('notes.view')}>
            <IconButton
              component="a"
              href={`/notes/${id}`}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              sx={{ ml: 1 }}
              aria-label={t('notes.view')}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        )}
        {updatedAt && (
            <Tooltip title={t('notes.lastUpdated', { time: formatAppDateTimeApi(updatedAt) ?? '' })}>
              <IconButton
                  size="small"
                  color="primary"
              >
                <AccessTimeIcon />
              </IconButton>
            </Tooltip>
        )}
        
        {/* Celebrations toggle (confetti + sound) in header */}
        <Tooltip title={t('notes.celebrateOnSave') || 'Celebrate on save'}>
          <FormControlLabel
            control={
              <Switch
                checked={celebrationsEnabled}
                onChange={handleToggleCelebrations}
                size="small"
                color="success"
              />
            }
            label={''}
            sx={{ ml: 1 }}
          />
        </Tooltip>
        <Tooltip
          title={
            editorMode === 'textarea'
              ? 'Plain textarea (TXT). Switch off for Jodit (WYSIWYG HTML).'
              : 'Jodit rich editor (HTML). Switch on for plain textarea (TXT).'
          }
        >
          <FormControlLabel
            control={
              <Switch
                checked={editorMode === 'textarea'}
                onChange={handleToggleEditorMode}
                size="small"
                color="primary"
              />
            }
            label={editorMode === 'textarea' ? 'TXT' : 'Jodit'}
            sx={{ ml: 1, userSelect: 'none' }}
          />
        </Tooltip>
        <Button
          size="small"
          variant="text"
          onClick={() => openLargePreview(editorMode === 'textarea' ? undefined : 'rendered')}
          sx={{ ml: 1, textTransform: 'none' }}
        >
          Open Large Preview
        </Button>
        {editorMode === 'jodit' && (
          <Tooltip title={t('notes.find') || 'Find in note'}>
            <IconButton
              size="small"
              color="primary"
              onClick={openFindDialog}
              sx={{ ml: 1 }}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('tips.shortcutsTooltip') || 'Keyboard shortcuts'}>
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => openShortcuts(e.currentTarget)}
            sx={{ ml: 1 }}
          >
            <KeyboardIcon />
          </IconButton>
        </Tooltip>

        {/* In-app find dialog (reusable) */}
        {editorMode === 'jodit' && (
          <FindInHtml
            open={findOpen}
            onClose={closeFindDialog}
            sourceHtml={originalDescription ?? description ?? ''}
            inline={!isMobile}
            onApply={(html) => {
              setOriginalDescription(originalDescription ?? description ?? '');
              setDescription(html);
            }}
            onClear={() => {
              clearHighlights();
            }}
            onFocusMatch={(index) => focusMatch(index)}
            containerSelector={'.jodit-wysiwyg'}
          />
        )}
      </Box>

      {/* Shortcuts popover (moved from Tips block) */}
      <Popover
        open={Boolean(shortcutsAnchorEl)}
        anchorEl={shortcutsAnchorEl}
        onClose={closeShortcuts}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ p: 1 }}
      >
        <Box sx={{ width: 320, p: 2 }}>
          <TextField
            fullWidth
            placeholder={t('tips.filterPlaceholder') || 'Filter shortcuts'}
            value={shortcutFilter}
            onChange={(e) => setShortcutFilter(e.target.value)}
            size="small"
            sx={{ mb: 1 }}
          />
            <Box sx={{ maxHeight: 'none', overflow: 'visible' }}>
              {[ 'editor', 'global' ].map((group) => {
                const items = shortcuts.filter(s => s.group === group && ( () => {
                  const f = shortcutFilter.trim().toLowerCase();
                  if (!f) return true;
                  return s.key.toLowerCase().includes(f) || s.desc.toLowerCase().includes(f);
                })());
                if (!items.length) return null;
                return (
                  <Box key={group} sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', pl: 0.5, display: 'block', mb: 0.5 }}>
                      { group === 'editor' ? t('shortcuts.editor') : t('shortcuts.global') }
                    </Typography>
                    {items.map((s) => (
                      <Box key={s.key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, alignItems: 'center' }}>
                        <Typography variant="body2">{s.desc}</Typography>
                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>{s.key}</Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
        </Box>
      </Popover>
      {/* Top progress bar during save */}
      {showTopProgress && (
        <LinearProgress
          variant="determinate"
          value={topProgress}
          sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1501, height: 4 }}
        />
      )}
      {/* Tips block removed â€” shortcuts are available via the keyboard icon in the header */}

      <form onSubmit={handleSubmit}>
        <MarkerHighlightTextField
          fullWidth
          label={t('notes.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => autosave({ name })}
          required
          disabled={isLoading}
          sx={{ mb: 2 }}
        />

        <Autocomplete
          freeSolo
          options={[...NOTE_STATUS_SUGGESTIONS]}
          value={status}
          onChange={(_event, value) => {
            const next = (value ?? '').trim();
            setStatus(next);
            void autosave({ status: next });
          }}
          onInputChange={(_event, value) => setStatus(value)}
          onBlur={() => void autosave({ status })}
          disabled={isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('notes.status')}
              placeholder={t('notes.statusPlaceholder')}
            />
          )}
          sx={{ mb: 2 }}
        />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t('notes.description')} *
          </Typography>
          {isEditorReady && (
            editorMode === 'textarea' ? (
              <TxtSplitEditor
                ref={txtSplitEditorRef}
                value={description || ''}
                onChange={handleTxtEditorChange}
                onBlur={onTextareaBlur}
                disabled={isLoading}
                layout="auto"
                showHtmlSourceStorageKey={NOTES_SHOW_HTML_SOURCE_KEY}
                showInsertTimestampButton
              />
            ) : (
              <MemoJoditEditor
                editorRef={editorRef}
                value={description || ''}
                config={config}
                onBlur={onJoditBlur}
              />
            )
          )}
          {descriptionError && (
            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
              {descriptionError}
            </Typography>
          )}
        </Box>

        {/* Large preview dialog (same idea as ExternalAssets) */}
        <Dialog
          open={txtLargePreviewOpen}
          onClose={() => setTxtLargePreviewOpen(false)}
          maxWidth={false}
          fullWidth
          PaperProps={{
            [LARGE_PREVIEW_DIALOG_ATTR]: '',
            sx: largePreviewModalPaperSx(largePreviewKeyboardContext, largePreviewThemed),
          }}
        >
          <DialogTitle
            sx={largePreviewDialogTitleSx(largePreviewKeyboardContext, largePreviewThemed)}
          >
            <LargePreviewDialogTitle
              previewMode={txtLargePreviewMode}
              keyboardContext={largePreviewKeyboardContext}
              modeToolbar={
                <LargePreviewModeToolbar
                  mode={txtLargePreviewMode}
                  onModeChange={setTxtLargePreviewMode}
                  showMediaGrid={txtPreviewCandidates.length > 0}
                />
              }
            />
          </DialogTitle>
          <DialogContent
            sx={
              txtLargePreviewMode === 'media'
                ? {
                    ...(largePreviewDialogContentSx(largePreviewKeyboardContext, largePreviewThemed) as any),
                    p: 0,
                    overflow: 'hidden',
                  }
                : largePreviewDialogContentSx(largePreviewKeyboardContext, largePreviewThemed)
            }
          >
            {txtLargePreviewMode === 'grid' ? (
              txtPreviewCandidates.length ? (
                <Box ref={txtLargePreviewGridRootRef} sx={largePreviewGridRootSx}>
                  {txtPreviewCandidates.map((src, idx) => (
                    <Box
                      key={`${src}-${idx}`}
                      data-grid-index={idx}
                      data-grid-selected={idx === txtLargePreviewIndex ? 'true' : 'false'}
                      sx={largePreviewGridTileSx(idx === txtLargePreviewIndex)}
                      onClick={() => setTxtLargePreviewIndex(idx)}
                    >
                      <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
                        {/* Clickable top bar to open large preview without starting playback */}
                        <Box
                          onClick={() => {
                            setTxtLargePreviewIndex(idx);
                            setTxtLargePreviewMode('media');
                          }}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 34,
                            zIndex: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1,
                            color: 'white',
                            cursor: 'pointer',
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.72), rgba(0,0,0,0))',
                            userSelect: 'none',
                          }}
                          title="Open large player (O)"
                        >
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            {idx + 1}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                              size="small"
                              variant="contained"
                              sx={{ textTransform: 'none', minHeight: 24, py: 0, px: 1 }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setTxtLargePreviewIndex(idx);
                                setTxtLargePreviewMode('media');
                              }}
                            >
                              Open (O)
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{
                                textTransform: 'none',
                                minHeight: 24,
                                py: 0,
                                px: 0.75,
                                bgcolor: 'rgba(0,0,0,0.35)',
                                borderColor: 'rgba(255,255,255,0.5)',
                                color: 'white',
                                '&:hover': { bgcolor: 'error.dark', borderColor: 'error.dark' },
                              }}
                              startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemovePreviewMedia(idx);
                              }}
                            >
                              {t('common.remove', { defaultValue: 'Remove' })}
                            </Button>
                          </Box>
                        </Box>
                        <iframe
                          ref={bindLargePreviewGridIframeRef(idx)}
                          {...previewIframeFocusHandlers}
                          src={withYoutubeEmbedJsApi(src)}
                          title={`Note media grid ${idx + 1}`}
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            border: 0,
                          }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                          allowFullScreen
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ p: 2, color: 'text.secondary' }}>No media to preview.</Box>
              )
            ) : txtLargePreviewMode === 'media' ? (
              txtLargePreviewEmbedUrl ? (
                <Box sx={largePreviewMediaPlayerShellSx(largePreviewKeyboardContext, largePreviewThemed)}>
                  {/* Left gutter (nav) */}
                  <Box sx={largePreviewSideGutterLeftSx(largePreviewKeyboardContext, largePreviewThemed)}>
                    <Tooltip title="Previous (←)">
                      <span style={{ width: '100%', flex: 1, display: 'flex', minHeight: 0 }}>
                        <IconButton
                          disabled={txtLargePreviewIndex <= 0}
                          onClick={() => setTxtLargePreviewIndex((i) => Math.max(0, i - 1))}
                          sx={largePreviewSideNavBtnSx(largePreviewKeyboardContext, largePreviewThemed)}
                          aria-label="Previous video"
                        >
                          <ChevronLeftIcon sx={{ fontSize: 32 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>

                  {/* Video */}
                  <Box sx={{ bgcolor: 'black' }}>
                    <iframe
                      ref={largePreviewIframeRef}
                      {...previewIframeFocusHandlers}
                      src={txtLargePreviewEmbedUrl}
                      title="Note media preview"
                      style={{ width: '100%', height: '100%', border: 0 }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                      allowFullScreen
                    />
                  </Box>

                  {/* Right gutter (actions + nav) */}
                  <Box sx={largePreviewSideGutterRightSx(largePreviewKeyboardContext, largePreviewThemed)}>
                    <Tooltip title={t('common.remove', { defaultValue: 'Remove' })}>
                      <span style={{ width: '100%', display: 'flex' }}>
                        <IconButton
                          color="error"
                          onClick={() => handleRemovePreviewMedia(txtLargePreviewIndex)}
                          sx={{
                            width: '100%',
                            minHeight: 48,
                            borderRadius: 1.5,
                            bgcolor: 'rgba(244, 67, 54, 0.12)',
                            border: '1px solid rgba(244, 67, 54, 0.35)',
                            boxShadow: '0 10px 20px rgba(244, 67, 54, 0.18)',
                            '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.18)' },
                          }}
                          aria-label="Remove"
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 26 }} />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Next (→)">
                      <span style={{ width: '100%', flex: 1, display: 'flex', minHeight: 0 }}>
                        <IconButton
                          disabled={txtLargePreviewIndex >= txtPreviewCandidates.length - 1}
                          onClick={() =>
                            setTxtLargePreviewIndex((i) =>
                              Math.min(txtPreviewCandidates.length - 1, i + 1),
                            )
                          }
                          sx={largePreviewSideNavBtnSx(largePreviewKeyboardContext, largePreviewThemed)}
                          aria-label="Next video"
                        >
                          <ChevronRightIcon sx={{ fontSize: 32 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ p: 2, color: 'text.secondary' }}>No media to preview.</Box>
              )
            ) : txtLargePreviewMode === 'raw' ? (
              <TextField
                value={normalizeJoditTempHtml(description || '')}
                fullWidth
                multiline
                minRows={24}
                InputProps={{ readOnly: true }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                  },
                }}
              />
            ) : (
              <Box
                sx={{
                  minHeight: '70vh',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  '& a': { color: 'primary.main' },
                }}
              >
                {renderDescriptionAsReact(
                  normalizeJoditTempHtml((description || '').trim()) ||
                    '<span style="color:#888;">No content to preview.</span>',
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={largePreviewDialogActionsBarSx(largePreviewKeyboardContext, largePreviewThemed)}
          >
            {largePreviewThemed && (
              <LargePreviewMediaActions
                previewMode={txtLargePreviewMode === 'grid' ? 'grid' : 'media'}
                candidateCount={txtPreviewCandidates.length}
                currentIndex={txtLargePreviewIndex}
                onPrev={() => setTxtLargePreviewIndex((i) => Math.max(0, i - 1))}
                onNext={() =>
                  setTxtLargePreviewIndex((i) =>
                    Math.min(txtPreviewCandidates.length - 1, i + 1),
                  )
                }
                canYoutubeSpace={canYoutubeSpace}
                isYoutubePlaying={isYoutubePlaying}
                onPlayClick={toggleYoutubePlayback}
                keyboardContext={largePreviewKeyboardContext}
                themed={largePreviewThemed}
              />
            )}
            <Button sx={largePreviewButtonFocusSx} onClick={() => setTxtLargePreviewOpen(false)}>
              {t('common.close') || 'Close'}
            </Button>
          </DialogActions>
        </Dialog>

        <RelatedNotesAutocomplete
          value={relatedNotes}
          onChange={handleRelatedNotesChange}
          disabled={isLoading}
          onBlur={() => autosave({ related: relatedNotes })}
        />

        <Box sx={{ mb: 2 }}>
          <TagsInput
            value={tagNames}
            size="small"
            suggestions={SUGGESTED_NOTE_TAGS}
            disabled={isLoading}
            onChange={(names) => {
              setTagNames(names);
              void autosave({ tags: names });
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Checkbox
            checked={notifyAboutChanges}
            onChange={(e) => {
              const checked = e.target.checked;
              setNotifyAboutChanges(checked);
              autosave({ notify: checked });
            }}
            disabled={isLoading}
          />
          <Typography>{t('notes.notifyAboutChanges')}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
          <DateTimePicker
            label={t('notes.reminderAt')}
            value={reminderAt}
            onChange={(newVal) => {
              setReminderAt(newVal);
            }}
            onAccept={(newVal) => {
              setReminderAt(newVal);
              autosave({ reminder: newVal ?? null });
            }}
            format={APP_DATETIME_FORMAT}
            ampm={false}
            minutesStep={1}
            disabled={isLoading}
            slotProps={{
              textField: { fullWidth: false, size: 'small', sx: { width: 240 } },
            }}
          />
          <Tooltip title={t('common.clear')}>
            <span>
              <IconButton
                aria-label="clear time"
                onClick={() => {
                  setReminderAt(null);
                  autosave({ reminder: null });
                }}
                disabled={isLoading || !reminderAt}
              >
                <ClearIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 4,
            alignItems: 'stretch',
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={isLoading}
            size="large"
            fullWidth={isMobile}
            sx={{
              height: 56,
              px: 3,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: '0 10px 18px rgba(0,0,0,0.18)',
            }}
          >
            {id ? t('notes.update') : t('notes.create')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/notes')}
            disabled={isLoading}
            size="large"
            fullWidth={isMobile}
            sx={{
              height: 56,
              px: 3,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              borderWidth: 2,
              '&:hover': { borderWidth: 2 },
            }}
          >
            {t('common.cancel')}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Copy notification */}
      <Snackbar
        open={copyNotification.open}
        autoHideDuration={2000}
        onClose={() => setCopyNotification({ open: false, text: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert 
          onClose={() => setCopyNotification({ open: false, text: '' })} 
          severity="success"
          sx={{ width: '100%' }}
        >
          âœ… Copied: &quot;{copyNotification.text}&quot;
        </Alert>
      </Snackbar>

      {id && (
          <Box sx={{ mt: 4 }}>
            {shouldLoadEmbeddedTasks && noteQuery.data ? (
              <Tasks
                key={id}
                noteId={id}
                linkedNoteSummary={{
                  id: String(noteQuery.data.id),
                  name: noteQuery.data.name,
                }}
              />
            ) : (
              <Box sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('tasks.title')}
                </Typography>
                <LinearProgress />
              </Box>
            )}
          </Box>
      )}
    </Box>
  );
}; 
